import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { Equals } from '../../../core/types';

export type BreakpointSize = 'sm' | 'md' | 'lg' /* | 'xl' */;
export type Breakpoint = {
  [S in BreakpointSize]: {
    current: S;
    sm: Equals<S, 'sm'>;
    md: Equals<S, 'md'>;
    lg: Equals<S, 'lg'>;
    // xl: Equals<S, 'xl'>;
  };
}[BreakpointSize];

const BreakpointContext = createContext<Breakpoint | undefined>(undefined);

export const useBreakpoint = () => {
  const context = useContext(BreakpointContext);
  if (context === undefined) {
    throw new Error(
      'useBreakpoint must be used within a <BreakpointProvider />'
    );
  }
  return context;
};

export const BreakpointProvider = ({
  children,
  initialBreakpoint = 'sm',
}: {
  children: React.ReactNode;
  initialBreakpoint?: BreakpointSize;
}) => {
  const [current, setCurrent] = useState<BreakpointSize>(initialBreakpoint);

  const listener = useCallback(() => {
    if (!window?.matchMedia) return;
    const cur = getBreakpoint();
    if (current !== cur) setCurrent(cur);
  }, [current]);

  useEffect(() => {
    listener();
    // -- invoke this bad boi once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window?.addEventListener !== 'function') return;
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [listener]);

  return (
    <BreakpointContext.Provider
      value={
        {
          current,
          sm: current === 'sm',
          md: current === 'md',
          lg: current === 'lg',
          // xl: current === 'xl',
        } as Breakpoint
      }
    >
      {children}
    </BreakpointContext.Provider>
  );
};

/**
 * Breakpoints come from DLS
 * https://design.aexp.com/v6/layout/breakpoints/
 * Since there is no breakpoint below "small" there is no point in matching anything below tablet.
 * Shop currently does not use the XL breakpoint, so those pieces are commented out.
 */
function getBreakpoint(): BreakpointSize {
  // if (window.matchMedia("(min-width: 1280px)").matches) return "xl";
  if (window.matchMedia('(min-width: 1024px)').matches) return 'lg';
  if (window.matchMedia('(min-width: 768px)').matches) return 'md';
  return 'sm';
}
